// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { useState } from 'react';
import { StyleSheet, View, type LayoutChangeEvent } from 'react-native';
import Svg, { Rect } from 'react-native-svg';

import { Text } from '@/components/Themed';
import appColors from '@/theme/colors';

import type { AnalysisStepProvider } from './useAnalysisSteps';

type Translate = (key: string, params?: Record<string, string | number>) => string;

/**
 * One provider's (or one job's) run drawn as the stages it went through.
 *
 * Deliberately driven by what the run recorded rather than by a fixed list of stage names: the
 * pipeline's stages are its own business and change (a canopy job records read-plane and trace,
 * a detection imagery, detect, merge and promote), and a hardcoded row of four boxes would
 * quietly stop matching the run it claims to draw. A stage name this build has no translation
 * for is shown as the name the analyser gave it, not as a key.
 *
 * Bar width is time, so the shape of a run is legible before a single number is read — fetching
 * pixels dominates a first analysis and all but disappears on a re-analysis that reads the
 * stored basemap. Rows of text with a pixel-sized bar, not one scaled drawing: scaled, the type
 * grew with the screen and the numbers fell off its edge.
 */

const NAME_WIDTH = 170;
const SECONDS_WIDTH = 70;
const BAR_HEIGHT = 16;
const MIN_BAR = 4;
const FALLBACK_WIDTH = 680;
const GAP = 8;

/** The translated stage name, or the analyser's own when this build has none for it. */
const stageName = (name: string, t: Translate): string => {
  const key = `batchFleetPage.stepName.${name}`;
  const translated = t(key);
  return translated === key ? name : translated;
};

export function AnalysisRunFlow({ provider, t }: { provider: AnalysisStepProvider; t: Translate }) {
  const [measured, setMeasured] = useState(0);
  const onLayout = (event: LayoutChangeEvent) => {
    const next = Math.floor(event.nativeEvent.layout.width);
    if (next > 0 && next !== measured) setMeasured(next);
  };

  const width = measured > 0 ? measured : FALLBACK_WIDTH;
  const trackWidth = Math.max(60, width - NAME_WIDTH - SECONDS_WIDTH - GAP * 2);
  const steps = provider.steps;
  const longest = Math.max(...steps.map((step) => step.seconds), 0.001);

  return (
    <View style={styles.wrap} testID="analysis-flow-provider" onLayout={onLayout}>
      <Text style={styles.providerName}>
        {[provider.provider, provider.providerVersion].filter(Boolean).join(' · ')}
      </Text>
      {provider.error ? <Text style={styles.error}>{provider.error}</Text> : null}

      {steps.map((step, index) => {
        const barWidth = Math.max(MIN_BAR, (step.seconds / longest) * trackWidth);
        const detail = Object.entries(step.detail)
          .map(([key, value]) => `${key} ${value}`)
          .join(' · ');
        return (
          <View key={`${step.name}-${index}`} style={styles.step}>
            <View style={styles.row}>
              <Text style={[styles.stepName, { width: NAME_WIDTH }]} numberOfLines={1}>
                {stageName(step.name, t)}
              </Text>
              <Svg width={trackWidth} height={BAR_HEIGHT}>
                <Rect
                  x={0}
                  y={1}
                  width={barWidth}
                  height={BAR_HEIGHT - 2}
                  rx={3}
                  fill={appColors.accent.primary}
                  fillOpacity={0.3}
                  stroke={appColors.accent.primary}
                  strokeOpacity={0.8}
                />
              </Svg>
              <Text style={styles.seconds} numberOfLines={1}>
                {t('batchFleetPage.stepsSeconds', { seconds: Math.round(step.seconds * 10) / 10 })}
              </Text>
            </View>
            {detail.length > 0 ? (
              <Text style={styles.detail} numberOfLines={2}>
                {detail}
              </Text>
            ) : null}
          </View>
        );
      })}
    </View>
  );
}

const styles = StyleSheet.create({
  wrap: {
    borderRadius: 12,
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: appColors.border.default,
    padding: 12,
    marginBottom: 12,
    gap: 6,
  },
  providerName: { color: appColors.text.primary, fontWeight: '600', fontSize: 15, marginBottom: 2 },
  error: { color: appColors.status.danger, fontSize: 13 },
  step: { gap: 2 },
  row: { flexDirection: 'row', alignItems: 'center', gap: GAP },
  stepName: { color: appColors.text.primary, fontSize: 13 },
  seconds: { color: appColors.text.muted, fontSize: 12, width: SECONDS_WIDTH },
  detail: { color: appColors.text.secondary, fontSize: 12, marginLeft: NAME_WIDTH + GAP },
});
